// src/pages/Families.jsx
import { useEffect, useMemo, useRef, useState, type FocusEvent, type ReactElement, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import PageTemplate from "../components/PageTemplate";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import Modal from "../components/Modal";
import { useTranslation } from "react-i18next";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import http from "../api/apiClient";
import { useCan } from "../authz/useCan";
import { familySituationLabel } from "../utils/familySituation";
import {
  FaArrowRight,
  FaChild,
  FaExclamationTriangle,
  FaFilter,
  FaPen,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import {
  getCachedFamilies,
  getFamiliesUiState,
  hasFamiliesCache,
  loadFamiliesOverview,
  removeCachedFamily,
  setFamiliesUiState,
} from "./familyOverviewCache";
import {
  getChildCount,
  hasNoChildren,
  hasNoParents,
  isEmptyFamily,
  matchesFamilySearch,
} from "./familyListUtils";
import { getBlockingChildCount, isFamilyDeleteBlocked } from "./familyDeleteUtils";


type Identifier = string | number;

type BadgeTone = "success" | "warn" | "danger" | "neutral";

interface FamilyRecord {
  id: Identifier;
  familyName?: string;
  familySituation?: string;
  parent1Id?: Identifier;
  parent2Id?: Identifier;
  parent1FirstName?: string;
  parent1LastName?: string;
  parent2FirstName?: string;
  parent2LastName?: string;
  childCount?: number;
  childrenCount?: number;
  children?: unknown[];
  kids?: unknown[];
  parents?: unknown[];
}

interface MappedError {
  message?: string;
  title?: string;
  status?: number;
}

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
}

const initialsForName = (name?: string): string =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("") || "?";

const Badge = ({ children, tone = "neutral" }: BadgeProps): ReactElement => {
  const tones: Record<BadgeTone, string> = {
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200",
    warn:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200",
    danger:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200",
    neutral:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Families = (): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cachedUiState = useMemo(() => getFamiliesUiState(), []);
  const cachedFamilies = useMemo(() => getCachedFamilies(), []);

  const [families, setFamilies] = useState<FamilyRecord[]>(() => (cachedFamilies ?? []) as FamilyRecord[]);
  const [searchQuery, setSearchQuery] = useState(cachedUiState.searchQuery);
  const [onlyEmpty, setOnlyEmpty] = useState(cachedUiState.onlyEmpty);
  const [onlyNoChildren, setOnlyNoChildren] = useState(cachedUiState.onlyNoChildren);
  const [onlyNoParents, setOnlyNoParents] = useState(cachedUiState.onlyNoParents);
  const [sortEmptyFirst, setSortEmptyFirst] = useState(cachedUiState.sortEmptyFirst);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(() => !hasFamiliesCache());
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState<MappedError | null>(null);
  const restoredScrollRef = useRef(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState<FamilyRecord | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [blockingChildCount, setBlockingChildCount] = useState<number | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [deletingFamily, setDeletingFamily] = useState(false);
  const canManageFamiliesLegacy = useCan("families", "crud");
  const canManageFamiliesDirect = useCan("families", "crud");
  const canManageFamiliesManage = useCan("families", "manage");
  const canManageFamilies = canManageFamiliesLegacy || canManageFamiliesDirect || canManageFamiliesManage;

  // 🆕 overlay-state
  const [showRotateOverlay, setShowRotateOverlay] = useState(false);
  const filterPopoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchFamilies = async () => {
      try {
        const data = await loadFamiliesOverview();
        if (!cancelled) setFamilies(data);
      } catch (err) {
        if (!cancelled && !hasFamiliesCache()) setError(mapHttpErrorToMessage(err, t) as MappedError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFamilies();
    return () => (cancelled = true);
  }, [t]);

  useEffect(() => {
    if (!loading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    setFamiliesUiState({
      searchQuery,
      onlyEmpty,
      onlyNoChildren,
      onlyNoParents,
      sortEmptyFirst,
    });
  }, [searchQuery, onlyEmpty, onlyNoChildren, onlyNoParents, sortEmptyFirst]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!restoredScrollRef.current && hasFamiliesCache()) {
      restoredScrollRef.current = true;
      window.requestAnimationFrame(() => window.scrollTo({ top: getFamiliesUiState().scrollY }));
    }

    let ticking = false;
    const persistScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setFamiliesUiState({ scrollY: window.scrollY });
        ticking = false;
      });
    };
    window.addEventListener("scroll", persistScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", persistScroll);
      setFamiliesUiState({ scrollY: window.scrollY });
    };
  }, []);

  useEffect(() => {
    if (!filterOpen || typeof document === "undefined") return undefined;

    const closeOnOutsideInteraction = (event: globalThis.MouseEvent | FocusEvent) => {
      const target = event.target;
      if (target instanceof Node && filterPopoverRef.current?.contains(target)) return;
      setFilterOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setFilterOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("focusin", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("focusin", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [filterOpen]);

  // 🆕: check viewport + orientation (zelfde aanpak als ChildrenOverview)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateOverlay = () => {
      const width = window.innerWidth;
      const isMobileLayout = width < 1024; // zelfde breakpoint als Tailwind lg
      const isPortrait =
        window.matchMedia?.("(orientation: portrait)")?.matches ?? false;

      setShowRotateOverlay(isMobileLayout && isPortrait);
    };

    updateOverlay();

    window.addEventListener("resize", updateOverlay);
    window.addEventListener("orientationchange", updateOverlay);

    return () => {
      window.removeEventListener("resize", updateOverlay);
      window.removeEventListener("orientationchange", updateOverlay);
    };
  }, []);

  const handleAddFamily = () => {
    if (!canManageFamilies) return;
    void navigate("/family/new");
  };
  const handleDetailsFamily = (id: Identifier): void => {
    void navigate(`/family/${id}/details`);
  };
  const handleEditFamily = (id: Identifier): void => {
    if (!canManageFamilies) return;
    void navigate(`/family/${id}/edit`);
  };
  const handleDetailsParent = (parentId?: Identifier): void => {
    if (parentId == null) return;
    void navigate(`/family/parent/${parentId}/details`);
  };

  const confirmDeleteFamily = (family: FamilyRecord): void => {
    if (!canManageFamilies) return;
    const childCount = getChildCount(family);
    setFamilyToDelete(family);
    setBlockingChildCount(childCount);
    setDeleteBlocked(childCount > 0);
    setDeleteError(null);
    setShowConfirm(true);
  };

  const handleDeleteFamily = async () => {
    if (!familyToDelete?.id || !canManageFamilies || deletingFamily || deleteBlocked) return;
    try {
      setDeletingFamily(true);
      setDeleteError(null);
      await http.delete(`/families/${familyToDelete.id}`);
      setFamilies((prev) => prev.filter((f) => f.id !== familyToDelete.id));
      removeCachedFamily(familyToDelete.id);
      setShowConfirm(false);
      setFamilyToDelete(null);
    } catch (err) {
      if (isFamilyDeleteBlocked(err)) {
        setDeleteBlocked(true);
        setBlockingChildCount(getBlockingChildCount(err));
        setDeleteError(null);
      } else {
        const mapped = mapHttpErrorToMessage(err, t) as MappedError;
        setDeleteError(mapped.message || t("family.delete.failed", { defaultValue: "Could not delete the family." }));
      }
    } finally {
      setDeletingFamily(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setFamilyToDelete(null);
    setDeleteError(null);
    setBlockingChildCount(null);
    setDeleteBlocked(false);
  };

  const filteredFamilies = useMemo(() => {
    return (families || [])
      .filter((f) => {
        if (!matchesFamilySearch(f, searchQuery)) return false;
        if (onlyNoChildren && !hasNoChildren(f)) return false;
        if (onlyNoParents && !hasNoParents(f)) return false;
        if (onlyEmpty && !isEmptyFamily(f)) return false;
        return true;
      })
      .sort((a, b) => {
        if (!sortEmptyFirst) return 0;
        return isEmptyFamily(a) === isEmptyFamily(b)
          ? (a.familyName || "").localeCompare(b.familyName || "", "nl")
          : isEmptyFamily(a)
          ? -1
          : 1;
      });
  }, [families, searchQuery, onlyEmpty, onlyNoChildren, onlyNoParents, sortEmptyFirst]);

  const duplicateParentKeys = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredFamilies.forEach((fam) => {
      const n1 = `${(fam.parent1FirstName || "").trim()} ${(fam.parent1LastName || "").trim()}`.trim().toLowerCase();
      const n2 = `${(fam.parent2FirstName || "").trim()} ${(fam.parent2LastName || "").trim()}`.trim().toLowerCase();
      if (!n1 || !n2) return;
      const key = [n1, n2].sort().join("||");
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [filteredFamilies]);

  const activeFilterCount = [onlyEmpty || onlyNoChildren || onlyNoParents].filter(Boolean).length;

  const clearFilters = () => {
    setOnlyEmpty(false);
    setOnlyNoChildren(false);
    setOnlyNoParents(false);
  };

  const familyFilterValue = onlyEmpty ? "empty" : onlyNoChildren ? "noChildren" : onlyNoParents ? "noParents" : "all";

  const setFamilyFilterValue = (value: string): void => {
    setOnlyEmpty(value === "empty");
    setOnlyNoChildren(value === "noChildren");
    setOnlyNoParents(value === "noParents");
  };

  if (loading) return showInitialSpinner ? <Spinner /> : null;

  if (error)
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );

  return (
    <PageTemplate
      title={null}
      wrapperClassName="max-w-none"
    >
      {/* 🔥 Zwarte overlay voor mobile portrait */}
      {showRotateOverlay && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-slate-950/90 px-6 text-center text-white backdrop-blur">
          <p className="mb-2 text-lg font-bold">
            {t("family.overview.rotate_title", {
              defaultValue: "Rotate your screen",
            })}
          </p>
          <p className="max-w-xs text-sm text-slate-200">
            {t("family.overview.rotate_message", {
              defaultValue:
                "This page works better in landscape mode or on a larger screen.",
            })}
          </p>
        </div>
      )}

      <div className="mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <SlidePageTransition className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                <FaUsers aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("family.overview.section_label", { defaultValue: "Family administration" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("family.overview.title", { defaultValue: "Family management" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("family.overview.subtitle", {
                    defaultValue: "Manage households, linked parents and children.",
                  })}
                </p>
              </div>
            </div>

            {canManageFamilies ? (
              <div className="ml-auto shrink-0 lg:mr-36">
                <button
                  type="button"
                  onClick={handleAddFamily}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                >
                  <FaPlus aria-hidden="true" />
                  {t("family.overview.add_button", { defaultValue: "Add family" })}
                </button>
              </div>
            ) : null}
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaUsers className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <label className="relative flex-1">
              <span className="sr-only">{t("family.overview.search_placeholder")}</span>
              <FaSearch aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("family.overview.search_placeholder")}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
              />
            </label>

            <div ref={filterPopoverRef} className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
                className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:w-auto ${
                  filterOpen || activeFilterCount > 0
                    ? "border-indigo-300 bg-indigo-600 text-white shadow-lg shadow-indigo-900/15 hover:bg-indigo-700 dark:border-indigo-400 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
                    : "border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                } ${activeFilterCount > 0 ? "pr-10" : ""}`}
                aria-expanded={filterOpen}
              >
                <span className="inline-flex h-full items-center justify-center gap-2 px-5">
                  <FaFilter aria-hidden="true" />
                  {t("common.filter", { defaultValue: "Filter" })}
                </span>
                {activeFilterCount > 0 && (
                  <span className="mr-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    clearFilters();
                    setFilterOpen(false);
                  }}
                  className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-white transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/70"
                  aria-label={t("common.clear", { defaultValue: "Clear" })}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}

              {filterOpen && (
                <div className="absolute right-0 z-[70] mt-3 w-[min(360px,calc(100vw-2rem))] rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-slate-900">
                  <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t("common.status", { defaultValue: "Status" })}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {[
                        { id: "all", label: t("family.filter.all", { defaultValue: "All families" }) },
                        { id: "noChildren", label: t("family.filter.no_children", { defaultValue: "Without children" }) },
                        { id: "noParents", label: t("family.filter.no_parents", { defaultValue: "Without parents" }) },
                        { id: "empty", label: t("family.filter.empty", { defaultValue: "Empty families" }) },
                      ].map((option) => {
                        const active = familyFilterValue === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => setFamilyFilterValue(option.id)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                              active
                                ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-200"
                                : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <label className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-indigo-600"
                      checked={sortEmptyFirst}
                      onChange={(e) => setSortEmptyFirst(e.target.checked)}
                    />
                    {t("family.overview.sort_empty_first", { defaultValue: "Empty families first" })}
                  </label>

                  <div className="mt-4 flex justify-between gap-2">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="rounded-full px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      {t("common.clear", { defaultValue: "Clear" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterOpen(false)}
                      className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    >
                      {t("common.close", { defaultValue: "Close" })}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-col gap-2 border-b border-slate-200 px-5 py-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("family.overview.list_label", { defaultValue: "Families" })}
              </p>
              <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                {t("family.overview.list_title", { defaultValue: "Family overview" })}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">
                {t("common.result_count", {
                  count: filteredFamilies.length,
                  defaultValue: "{{count}} results",
                })}
              </Badge>
              {activeFilterCount > 0 ? (
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t("family.overview.filtered_result", {
                    defaultValue: "Filtered from {{count}} total",
                    count: families.length,
                  })}
                </span>
              ) : null}
            </div>
          </div>

          {filteredFamilies.length > 0 ? (
            <div className="divide-y divide-slate-200/80 dark:divide-white/10">
              {filteredFamilies.map((f) => {
                const childCount = getChildCount(f);
                const emptyState = isEmptyFamily(f);
                const noParents = hasNoParents(f);
                const n1 = `${(f.parent1FirstName || "").trim()} ${(f.parent1LastName || "").trim()}`.trim().toLowerCase();
                const n2 = `${(f.parent2FirstName || "").trim()} ${(f.parent2LastName || "").trim()}`.trim().toLowerCase();
                const duplicateKey = n1 && n2 ? [n1, n2].sort().join("||") : null;
                const isDuplicateParents = duplicateKey && duplicateParentKeys[duplicateKey] > 1;
                const parentOne = [f.parent1FirstName, f.parent1LastName].filter(Boolean).join(" ").trim();
                const parentTwo = [f.parent2FirstName, f.parent2LastName].filter(Boolean).join(" ").trim();

                return (
                  <article
                    key={f.id}
                    className="group grid cursor-pointer gap-4 px-5 py-4 transition hover:bg-slate-50/80 dark:hover:bg-white/[0.035] lg:grid-cols-[minmax(240px,1fr)_minmax(270px,1.15fr)_140px_180px_auto] lg:items-center"
                    onClick={() => handleDetailsFamily(f.id)}
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-indigo-100 bg-indigo-50 text-sm font-bold text-indigo-700 shadow-sm dark:border-indigo-400/20 dark:bg-indigo-400/10 dark:text-indigo-200">
                        {initialsForName(f.familyName)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-base font-bold text-slate-950 dark:text-white">{f.familyName || "—"}</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {familySituationLabel(t, f.familySituation) || t("family.table.family_situation", { defaultValue: "Family situation" })}
                        </p>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <FaUsers className="shrink-0 text-sky-500" aria-hidden="true" />
                        {parentOne ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDetailsParent(f.parent1Id);
                            }}
                            className="truncate text-sky-700 transition hover:text-sky-900 dark:text-sky-200 dark:hover:text-white"
                          >
                            {parentOne}
                          </button>
                        ) : null}
                        {parentOne && parentTwo ? <span className="text-slate-400">&</span> : null}
                        {parentTwo ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDetailsParent(f.parent2Id);
                            }}
                            className="truncate text-sky-700 transition hover:text-sky-900 dark:text-sky-200 dark:hover:text-white"
                          >
                            {parentTwo}
                          </button>
                        ) : null}
                        {!parentOne && !parentTwo ? <span className="text-slate-500 dark:text-slate-400">—</span> : null}
                      </div>
                      {isDuplicateParents && (
                        <div className="mt-2">
                          <Badge tone="warn">
                            <span className="inline-flex items-center gap-1">
                              <FaExclamationTriangle aria-hidden="true" />
                              {t("family.table.same_parents", { defaultValue: "Same parents" })}
                            </span>
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("family.table.children", { defaultValue: "Children" })}
                      </p>
                      <Badge tone={childCount > 0 ? "success" : "warn"}>
                        <span className="inline-flex items-center gap-1">
                          <FaChild aria-hidden="true" />
                          {childCount}
                        </span>
                      </Badge>
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("common.status", { defaultValue: "Status" })}
                      </p>
                      {emptyState ? (
                        <Badge tone="danger">{t("family.filter.empty", { defaultValue: "Empty family" })}</Badge>
                      ) : noParents ? (
                        <Badge tone="warn">{t("family.filter.no_parents", { defaultValue: "No parents" })}</Badge>
                      ) : childCount === 0 ? (
                        <Badge tone="warn">{t("family.filter.no_children", { defaultValue: "No children" })}</Badge>
                      ) : (
                        <Badge tone="success">{t("common.active", { defaultValue: "Active" })}</Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDetailsFamily(f.id);
                        }}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 text-xs font-bold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-400 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200"
                      >
                        {t("common.actions.view", { defaultValue: "View" })}
                        <FaArrowRight aria-hidden="true" />
                      </button>

                      {canManageFamilies && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFamily(f.id);
                          }}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-indigo-200 bg-white text-indigo-600 transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/25 dark:bg-white/5 dark:text-indigo-200"
                          aria-label={t("common.actions.edit", { defaultValue: "Edit" })}
                        >
                          <FaPen aria-hidden="true" />
                        </button>
                      )}

                      {canManageFamilies && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDeleteFamily(f);
                          }}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-400/25 dark:bg-white/5 dark:text-red-200"
                          aria-label={t("common.actions.delete", { defaultValue: "Delete" })}
                        >
                          <FaTrash aria-hidden="true" />
                        </button>
                      )}

                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-slate-100 text-2xl text-slate-400 dark:bg-white/5 dark:text-slate-500">
                <FaUsers aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-lg font-bold text-slate-950 dark:text-white">
                {t("family.overview.none_available", { defaultValue: "No families found" })}
              </h3>
            </div>
          )}
        </section>
        </SlidePageTransition>
      </div>

      <Modal
        show={showConfirm}
        title={
          deleteBlocked
            ? t("family.delete.blocked_title", { defaultValue: "Family cannot be deleted" })
            : t("family.delete.title", { defaultValue: "Permanently delete family?" })
        }
        type={deleteBlocked ? "warning" : "confirm"}
        onClose={cancelDelete}
        dialogClassName="!w-[min(38rem,calc(100vw-2rem))] !max-w-xl text-left"
        footer={
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={cancelDelete}
              disabled={deletingFamily}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              {deleteBlocked
                ? t("common.close", { defaultValue: "Close" })
                : t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            {deleteBlocked ? (
              <button
                type="button"
                onClick={() => familyToDelete?.id && navigate(`/family/${familyToDelete.id}/details`)}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                {t("family.delete.view_family", { defaultValue: "View family and children" })}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDeleteFamily}
                disabled={deletingFamily}
                className="rounded-xl border border-red-300 bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deletingFamily
                  ? t("family.delete.busy", { defaultValue: "Deleting..." })
                  : t("family.delete.confirm", { defaultValue: "Delete permanently" })}
              </button>
            )}
          </div>
        }
      >
        <div className="space-y-4 text-left">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {deleteBlocked
              ? t("family.delete.blocked_message", {
                  count: blockingChildCount ?? t("family.delete.some_children", { defaultValue: "one or more" }),
                  defaultValue:
                    blockingChildCount === 1
                      ? "This family cannot be deleted because 1 child is still linked. Move or process this child first."
                      : "This family cannot be deleted because {{count}} children are still linked. Move or process these children first.",
                })
              : t("family.delete.warning", {
                  defaultValue: "Deleting this family is permanent and cannot be undone. Children are never deleted automatically.",
                })}
          </p>
          {familyToDelete && (
            <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 font-semibold text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
              {familyToDelete.familyName || familyToDelete.id}
            </p>
          )}
          {deleteError && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{deleteError}</p>}
        </div>
      </Modal>
    </PageTemplate>
  );
};

export default Families;
